import { concatMap, EMPTY, filter, forkJoin, from, map, mergeMap, of, toArray, zip } from "rxjs";
import { OfflineUtilsService } from "../../../_offline/services/offline-utils.service";
import { AnnotationImageService } from "../../http-api/annotation-image.service";
import { OfflineManagementService } from "../../offline-management.service";
import { OnPremiseSyncDataState } from "./on-premise-sync-state.service";
import { AnnotationImageDetail, AnnotationObjectForUpdate } from "../../../_objects/inspection-image-annotation";
import { InspectionImageSchema } from "../../../_offline/models/inspectionImage.schema";
import { InspectionDbService } from "../../database/inspection-db.service";
import { InspectionImageDbService } from "../../database/inspection-image-db.service";
import { USE_IMAGE_TYPE } from "../../../app.constants";
import { Injectable } from "@angular/core";
import { InspectionSchema } from "../../../_offline/models/inspection.schema";

type ModifiedFiles = {
  modified: File | null,
  modifiedThumbnail: File | null
  measurement: File | null,
  measurementThumbnail: File | null,
}

@Injectable({
  providedIn: 'root'
})
export class ModifiedImagesFirstSyncService {
  constructor(
    private syncState: OnPremiseSyncDataState,
    private offlineUtilService: OfflineUtilsService,
    private offlineManagementService: OfflineManagementService,
    private annotationService: AnnotationImageService,
    private inspectionDbService: InspectionDbService,
    private inspectionImageDbService: InspectionImageDbService
  ) { }

  public syncModifiedImages() {
    return this.inspectionDbService.getAllOnPremise().pipe(
          concatMap(inspection => inspection),
          mergeMap((inspection) => this.getAllImages(inspection)),
          mergeMap(({ images, inspection }) => this.syncAllModifiedImages(images, inspection.id)),
          toArray()
    );
  }

  private getAllImages(inspection: InspectionSchema) {
    return this.inspectionImageDbService.getAllOnPremise(inspection.id).pipe(map((images) => ({ images, inspection })),);
  }

   private syncAllModifiedImages(imagesSchemas: InspectionImageSchema[], inspectionId: string) {
      return from(imagesSchemas).pipe(
        mergeMap((image) => this.syncModifiedImage(image, inspectionId)),
        toArray()
      );
    }

    private syncModifiedImage(image: InspectionImageSchema, inspectionIdSchema: string) {
      const imageId = this.syncState.getInspectionImageId(image.id);
      const inspectionId = this.syncState.getInspectionId(inspectionIdSchema);

      return forkJoin({
        modified: this.offlineUtilService.getModifiedImageObj(image),
        modifiedThumbnail:this.offlineUtilService.getModifiedThumbnailImageObj(image),
        measurement:this.offlineUtilService.getMeasurementImageObj(image),
        measurementThumbnail: this.offlineUtilService.getMeasurementThumbnailImageObj(image),
    })
      .pipe(
        mergeMap((files) => this.sendModifiedImages(inspectionId, imageId, files, image)),
      );
    }

    private sendRetouchedAnnotations(inspectionId: string, imageId: string, image: InspectionImageSchema) {
      if (!image.modifiedItems) {
        return of(new AnnotationImageDetail());
      }
      const parsedAnnotations = image.modifiedItems ? JSON.parse(image.modifiedItems): null;
      const annotationReq = new AnnotationObjectForUpdate(imageId, parsedAnnotations, null, image.schemaVersion);
      return this.annotationService.putImageAnnotations(annotationReq, inspectionId, 1);
    }

    private sendMeasurementAnnotations(inspectionId: string, imageId: string, image: InspectionImageSchema) {
      if (!image.measurementItems) {
        return of(new AnnotationImageDetail());
      }
      const parsedMeasurements = image.measurementItems ? JSON.parse(image.measurementItems): null;
      const annotationReq = new AnnotationObjectForUpdate(imageId, null, parsedMeasurements, image.schemaVersion);
      return this.annotationService.putImageAnnotations(annotationReq, inspectionId, 2);
    }

    private sendModifiedImages(inspectionId: string, imageId: string, files: ModifiedFiles, image: InspectionImageSchema) {
     return this.sendRetouchedAnnotations(inspectionId, imageId, image).pipe(
        concatMap(() => this.sendMeasurementAnnotations(inspectionId, imageId, image)),
        concatMap(() => this.sendFileIfExists(inspectionId, imageId, files.modifiedThumbnail, USE_IMAGE_TYPE.RETOUCHED_THUMBNAIL)),
        concatMap(() => this.sendFileIfExists(inspectionId, imageId, files.measurement, USE_IMAGE_TYPE.REMEASUREMENT)),
        concatMap(() => this.sendFileIfExists(inspectionId, imageId, files.measurementThumbnail, USE_IMAGE_TYPE.REMEASUREMENT_THUMBNAIL)),
        concatMap(() => this.sendFileIfExists(inspectionId, imageId, files.modified, USE_IMAGE_TYPE.RETOUCHED))
      );
    }

    private sendFileIfExists(inspectionId: string, imageId: string, file: File | null, imageType: USE_IMAGE_TYPE) {
      if (!file) {
        return of(new AnnotationImageDetail());
      }

      return this.offlineManagementService.putOfflineInspectionModifiedImageBinary(inspectionId, imageId, file, imageType);
    }

}
